import React, { useMemo } from 'react';

// --- ICONOS SVG PREMIUM ---
const IconTramite = () => (
    <svg className="w-5 h-5 text-indigo-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
);

const IconPending = () => (
    <svg className="w-5 h-5 text-amber-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
);

const IconReclamado = () => (
    <svg className="w-5 h-5 text-rose-600 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.5">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
);

const IconUser = () => (
    <svg className="w-4 h-4 text-indigo-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
);

const IconSettings = () => (
    <svg className="w-4 h-4 text-indigo-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2.2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
);

/**
 * Componente modular de lista analítica para distribuciones operativas
 * Reemplaza las tablas rígidas por renglones Flexbox fluidos y espaciosos con efectos hover
 */
const AnalyticsList = ({ title, icon, data, badgeConfig = { bg: 'bg-indigo-50 border-indigo-100', text: 'text-indigo-700', label: 'casos' } }) => (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-[0_2px_12px_-3px_rgba(0,0,0,0.02)] overflow-hidden text-left flex flex-col h-full transition-all duration-200 hover:shadow-md">
        {/* Cabecera superior con degradado suave */}
        <div className="p-4 px-5 bg-gradient-to-r from-slate-50 to-white border-b border-slate-100 font-black text-[10px] text-slate-500 uppercase tracking-widest flex items-center gap-2 select-none">
            {icon} 
            <span>{title}</span>
        </div>
        
        {/* Cuerpo de la Lista */}
        <div className="divide-y divide-slate-100 max-h-[380px] overflow-y-auto custom-scrollbar bg-white">
            {data.length === 0 ? (
                <div className="p-12 text-center text-slate-400 italic text-[11px] font-semibold select-none">
                    Sin registros para clasificar en este rango
                </div>
            ) : (
                data.map((row, idx) => (
                    <div 
                        key={idx} 
                        className="p-4 px-5 flex justify-between items-center hover:bg-slate-50/40 transition-colors duration-150 group"
                    >
                        {/* Izquierda: Ramo / Compañía (Nombre en negrita) */}
                        <div className="flex flex-col text-left min-w-0 pr-4">
                            <span className="text-[12px] font-black uppercase text-slate-800 tracking-tight truncate group-hover:text-blue-900 transition-colors">
                                {row.name}
                            </span>
                        </div>
                        
                        {/* Derecha: Contador de casos en badge de alto contraste */}
                        <div className="flex flex-col items-end shrink-0 select-none">
                            <span className={`inline-flex items-center justify-center px-3 py-1 border rounded-full font-mono text-[10px] font-black ${badgeConfig.bg} ${badgeConfig.text}`}>
                                {row.cant} {row.cant === 1 ? badgeConfig.label.slice(0, -1) : badgeConfig.label}
                            </span>
                        </div>
                    </div>
                ))
            )}
        </div>
    </div>
);

/**
 * KpisEndosos - Dashboard analítico de endosos y trámites operativos.
 */
const KpisEndosos = ({ registros, isDbLoaded }) => {

    // --- PROCESAMIENTO ANALÍTICO EN MEMORIA (BI ENGINE) ---
    const metrics = useMemo(() => {
        if (!isDbLoaded || !registros || registros.length === 0) {
            return {
                totalTramites: 0,
                cantPendiente: 0,
                cantReclamado: 0,
                porTipoGestion: [],
                porEstadoGestion: [],
                porResponsable: []
            };
        }

        const totalTramites = registros.length;
        let cantPendiente = 0;
        let cantReclamado = 0;
        const tiposMap = {};
        const estadosMap = {};
        const responsablesMap = {};

        registros.forEach(item => {
            const tipo = item.tipoGestion || 'ENDOSO GENERAL';
            tiposMap[tipo] = (tiposMap[tipo] || 0) + 1;

            const estado = item.estadoGestion || 'PENDIENTE';
            estadosMap[estado] = (estadosMap[estado] || 0) + 1;

            const resp = item.quienGestiono || item.quienGestionoEndoso || item.usuarioId || 'SIN ASIGNAR';
            responsablesMap[resp] = (responsablesMap[resp] || 0) + 1;

            // Clasificación de estados críticos de control operativo
            const estadoLimpio = String(estado).trim().toLowerCase();
            if (estadoLimpio === 'pendiente') {
                cantPendiente += 1;
            }
            if (estadoLimpio === 'reclamado' || estadoLimpio === 'reclamada') {
                cantReclamado += 1;
            }
        });

        return {
            totalTramites,
            cantPendiente,
            cantReclamado,
            porTipoGestion: Object.entries(tiposMap)
                .map(([name, cant]) => ({ name, cant }))
                .sort((a, b) => b.cant - a.cant),
            porEstadoGestion: Object.entries(estadosMap)
                .map(([name, cant]) => ({ name, cant }))
                .sort((a, b) => b.cant - a.cant),
            porResponsable: Object.entries(responsablesMap)
                .map(([name, cant]) => ({ name, cant }))
                .sort((a, b) => b.cant - a.cant)
        };
    }, [registros, isDbLoaded]);

    if (!isDbLoaded) {
        return (
            <div className="p-8 text-center text-slate-400 italic text-sm">
                Cargando análisis de endosos...
            </div>
        );
    }

    if (registros.length === 0) {
        return (
            <div className="p-12 text-center text-slate-400 italic text-sm bg-white rounded-2xl border border-slate-200">
                No se registraron endosos o trámites operativos en este período de fechas.
            </div>
        );
    }

    return (
        <div className="space-y-8 text-left animate-fade-in">
            
            {/* GRUPO I: KPIs GLOBALES DE GESTIÓN OPERATIVA */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                
                {/* TRÁMITES TOTALES */}
                <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Trámites Gestionados</p>
                        <h4 className="text-xl font-black text-slate-800 font-mono mt-2">{metrics.totalTramites}</h4>
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Total de endosos procesados</p>
                    </div>
                    <span className="p-3 bg-indigo-50 text-indigo-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center">
                        <IconTramite />
                    </span>
                </div>

                {/* TRÁMITES PENDIENTES */}
                <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Trámites Pendientes</p>
                        <h4 className="text-xl font-black text-amber-600 font-mono mt-2">{metrics.cantPendiente}</h4>
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Esperando resolución</p>
                    </div>
                    <span className="p-3 bg-amber-50 text-amber-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center">
                        <IconPending />
                    </span>
                </div>

                {/* TRÁMITES RECLAMADOS */}
                <div className="bg-white p-5 border border-slate-200 rounded-2xl shadow-sm flex items-center justify-between transition-all hover:shadow-md">
                    <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest leading-none">Trámites Reclamados</p>
                        <h4 className="text-xl font-black text-rose-600 font-mono mt-2">{metrics.cantReclamado}</h4>
                        <p className="text-[9px] text-slate-400 mt-1.5 font-bold uppercase tracking-wider">Gestiones con reclamo activo</p>
                    </div>
                    <span className="p-3 bg-rose-50 text-rose-600 rounded-xl shrink-0 w-11 h-11 flex items-center justify-center">
                        <IconReclamado />
                    </span>
                </div>

            </div>

            {/* SECCIÓN II: TABLEROS DE DISTRIBUCIÓN OPERATIVA EN LISTA FLEXBOX */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-2">
                
                {/* 1. TRÁMITES POR SUSCRIPTOR */}
                <AnalyticsList 
                    title="Desempeño de Suscriptores"
                    icon={<IconUser />}
                    data={metrics.porResponsable}
                    badgeConfig={{ bg: 'bg-indigo-50 border-indigo-100', text: 'text-indigo-700', label: 'trámites' }}
                />

                {/* 2. DISTRIBUCIÓN POR TIPO DE GESTIÓN */}
                <AnalyticsList 
                    title="Distribución por Tipo de Gestión"
                    icon={<IconSettings />}
                    data={metrics.porTipoGestion}
                    badgeConfig={{ bg: 'bg-slate-50 border-slate-200', text: 'text-slate-600', label: 'casos' }}
                />

                {/* 3. ESTADOS DE GESTIÓN */}
                <AnalyticsList 
                    title="Concentración por Estado"
                    icon={<IconSettings />}
                    data={metrics.porEstadoGestion}
                    badgeConfig={{ bg: 'bg-amber-50 border-amber-200', text: 'text-amber-800', label: 'operaciones' }}
                />

            </div>

            {/* SOPORTE DE SCROLLBARS Y TRANSICIONES */}
            <style>{`
                .custom-scrollbar::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
                .custom-scrollbar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 10px; }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
                .animate-fade-in { animation: fadeIn 0.25s ease-out forwards; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </div>
    );
};

export default KpisEndosos;